import { createGraphRuntime } from '../../graph/graphRuntime.js';
import type { GraphRuntimeRenderSettings } from '../../graph/graphRuntime.js';
import { frameLayoutDirection as getFrameLayoutDirection, type LayoutDirection } from '../../scene/frameLayout.js';
import type { FrameLayout, Scene, SceneNodeId } from '../../scene/sceneState.js';
import { resolveGeometrySlotFor, resolveMaterialSlotFor } from '../../state/layerGraphSelectors.js';
import type { AppState } from '../../state/appState.js';

export function getActiveScene(appState: AppState): Scene {
  if (appState.activeComponentId) {
    const comp = appState.componentLibrary[appState.activeComponentId];
    if (comp) return comp.scene;
  }
  return appState.scene;
}

export function frameLayoutDirection(_appState: AppState, layout: FrameLayout): LayoutDirection {
  return getFrameLayoutDirection(layout);
}

export function findParentFrameId(scene: Scene, nodeId: SceneNodeId): SceneNodeId | null {
  for (const [id, node] of Object.entries(scene.nodes)) {
    if (node.kind === 'frame' && node.childIds.includes(nodeId)) {
      return id;
    }
  }
  return null;
}

export function findInitialSelectionForActiveComponent(scene: Scene, componentId: string | null): SceneNodeId | null {
  if (!componentId) return null;

  function visit(nodeId: SceneNodeId, nearestFrameId: SceneNodeId | null): SceneNodeId | null {
    const node = scene.nodes[nodeId];
    if (!node) return null;

    if (node.kind === 'instance' && node.componentId === componentId) {
      return nearestFrameId ?? nodeId;
    }

    if (node.kind === 'frame') {
      const nextNearestFrameId = nodeId;
      for (const childId of node.childIds) {
        const match = visit(childId, nextNearestFrameId);
        if (match) return match;
      }
    }

    return null;
  }

  for (const rootId of scene.rootIds) {
    const match = visit(rootId, scene.nodes[rootId]?.kind === 'frame' ? rootId : null);
    if (match) return match;
  }

  return null;
}

export function getActiveContainerId(scene: Scene, nodeId: SceneNodeId | null): SceneNodeId | null {
  if (!nodeId) return null;
  const node = scene.nodes[nodeId];
  if (!node) return null;
  if (node.kind === 'frame') return nodeId;
  return findParentFrameId(scene, nodeId);
}

export function componentReferencesComponent(
  appState: AppState,
  sourceComponentId: string,
  targetComponentId: string,
  visited = new Set<string>(),
): boolean {
  if (sourceComponentId === targetComponentId) return true;
  if (visited.has(sourceComponentId)) return false;
  visited.add(sourceComponentId);
  const source = appState.componentLibrary[sourceComponentId];
  if (!source) return false;
  for (const node of Object.values(source.scene.nodes)) {
    if (node.kind !== 'instance') continue;
    if (node.componentId === targetComponentId) return true;
    if (componentReferencesComponent(appState, node.componentId, targetComponentId, visited)) return true;
  }
  return false;
}

export function measureLayerIntrinsicSize(
  appState: AppState,
  layerId: string,
  renderTheme?: GraphRuntimeRenderSettings['renderTheme'],
): { w: number; h: number } | null {
  const layerDef = appState.layerLibrary[layerId];
  if (!layerDef) return null;
  const geometry = resolveGeometrySlotFor(appState, layerDef.geometry);
  const material = resolveMaterialSlotFor(appState, layerDef.material);
  if (!geometry) return null;
  const runtime = createGraphRuntime(
    geometry.nodes,
    geometry.edges,
    material,
    {
      graphId: layerId,
      renderSettings: {
        colormap: appState.colormap,
        aaMode: appState.aaMode,
        aaWidth: appState.aaWidth,
        arcCoverageMode: appState.arcCoverageMode,
        renderTheme,
      },
      sizeMode: geometry.sizeMode,
      shapes: { ...appState.builtInShapes, ...appState.shapes },
    },
  );
  return runtime.measure({ ...appState.params }, {
    min: { w: 0, h: 0 },
    max: { w: appState.resolution, h: appState.resolution },
  });
}
